import { useState, useEffect, useCallback } from "react";
import { Categorie, BaseCategorie } from "../../../types/features/Post";
import { categorieService } from "../../../services/categorieService";
import useToken from "../auth/useToken";

export default function useCategories() {
  const { token } = useToken();
  const [categories, setCategories] = useState<Categorie[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0,
    perPage: 20,
  });

  // 🔓 PUBLIC - Pas de token nécessaire
  const fetchCategories = useCallback(async (page: number = 1, perPage: number = 20) => {
    setLoading(true);
    setError(null);
    try {
      const response = await categorieService.getAll(page, perPage);
      setCategories(response.data);
      setPagination({
        currentPage: response.current_page,
        lastPage: response.last_page,
        total: response.total,
        perPage: response.per_page,
      });
    } catch (err) {
      setError("Erreur lors du chargement des catégories");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // 🔓 PUBLIC - Pas de token nécessaire
  const fetchCategorieById = useCallback(async (id: string): Promise<Categorie | null> => {
    setLoading(true);
    setError(null);
    try {
      const categorie = await categorieService.getById(id);
      if (!categorie) {
        setError("Catégorie non trouvée");
      }
      return categorie;
    } catch (err) {
      setError("Erreur lors du chargement de la catégorie");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // 🔒 PROTÉGÉ - Token requis
  const createCategorie = useCallback(async (data: BaseCategorie): Promise<Categorie | null> => {
    if (!token) {
      setError("Vous devez être connecté");
      return null;
    }

    setLoading(true);
    setError(null);
    try {
      const newCategorie = await categorieService.create(token, data);
      setCategories((prev) => [...prev, newCategorie]);
      return newCategorie;
    } catch (err) {
      setError("Erreur lors de la création de la catégorie");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔒 PROTÉGÉ - Token requis
  const updateCategorie = useCallback(async (id: string, data: Partial<BaseCategorie>): Promise<Categorie | null> => {
    if (!token) {
      setError("Vous devez être connecté");
      return null;
    }

    setLoading(true);
    setError(null);
    try {
      const updatedCategorie = await categorieService.update(token, id, data);
      setCategories((prev) => prev.map((c) => (c.id === id ? updatedCategorie : c)));
      return updatedCategorie;
    } catch (err) {
      setError("Erreur lors de la mise à jour de la catégorie");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔒 PROTÉGÉ - Token requis
  const deleteCategorie = useCallback(async (id: string): Promise<boolean> => {
    if (!token) {
      setError("Vous devez être connecté");
      return false;
    }

    setLoading(true);
    setError(null);
    try {
      await categorieService.delete(token, id);
      setCategories((prev) => prev.filter((c) => c.id !== id));
      return true;
    } catch (err) {
      setError("Erreur lors de la suppression de la catégorie");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔒 PROTÉGÉ - Token requis
  const deleteManyCategories = useCallback(async (ids: string[]): Promise<boolean> => {
    if (!token) {
      setError("Vous devez être connecté");
      return false;
    }

    setLoading(true);
    setError(null);
    try {
      await categorieService.deleteMany(token, ids);
      setCategories((prev) => prev.filter((c) => !ids.includes(c.id)));
      return true;
    } catch (err) {
      setError("Erreur lors de la suppression multiple");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  return {
    categories,
    loading,
    error,
    pagination,
    fetchCategories,
    fetchCategorieById,
    createCategorie,
    updateCategorie,
    deleteCategorie,
    deleteManyCategories,
  };
}